/**
 * Next auto-generated draft invoice date for a project's billing schedule.
 *
 * Mirrors the backend AutoInvoiceSchedule used by AutoInvoiceGenerationJob: weekly and
 * fortnightly key off the configured day of week, monthly and quarterly off the monthly
 * pattern, and fortnightly/quarterly additionally require a minimum gap since the last
 * generated draft. Frequencies without a schedule (on-completion, milestone) return null.
 */

const DAYS_OF_WEEK = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const FORTNIGHTLY_MINIMUM_DAY_GAP = 14;
const QUARTERLY_MINIMUM_MONTH_GAP = 3;

export interface AutoInvoiceScheduleSettings {
  billingFrequency: string;
  monthlyPattern: string;
  monthlyDay: string;
  weeklyDay: string;
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const daysBetween = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000);

const monthsBetween = (from: Date, to: Date) =>
  (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth();

function nextConfiguredWeekday(weeklyDay: string, from: Date): Date {
  const targetDay = DAYS_OF_WEEK.indexOf(weeklyDay);
  if (targetDay === -1) return startOfDay(from);

  let daysUntilTarget = targetDay - from.getDay();
  if (daysUntilTarget <= 0) daysUntilTarget += 7;

  const nextDate = startOfDay(from);
  nextDate.setDate(nextDate.getDate() + daysUntilTarget);
  return nextDate;
}

function monthlyPatternDate(pattern: string, monthlyDay: string, year: number, month: number): Date {
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

  switch (pattern) {
    case "last-day":
      return new Date(year, month, lastDayOfMonth);

    case "first-working-day": {
      const date = new Date(year, month, 1);
      while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
      return date;
    }

    case "last-working-day": {
      const date = new Date(year, month, lastDayOfMonth);
      while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() - 1);
      return date;
    }

    default: {
      const day = parseInt(monthlyDay, 10);
      return new Date(year, month, Math.min(Number.isNaN(day) ? 1 : day, lastDayOfMonth));
    }
  }
}

export function getNextAutoInvoiceDate(
  settings: AutoInvoiceScheduleSettings,
  lastAutoGeneratedAt: string | Date | null = null,
  from: Date = new Date(),
): Date | null {
  const lastGenerated = lastAutoGeneratedAt ? new Date(lastAutoGeneratedAt) : null;
  const today = startOfDay(from);

  switch (settings.billingFrequency) {
    case "weekly":
      return nextConfiguredWeekday(settings.weeklyDay, today);

    case "fortnightly": {
      const nextDate = nextConfiguredWeekday(settings.weeklyDay, today);
      while (lastGenerated && daysBetween(lastGenerated, nextDate) < FORTNIGHTLY_MINIMUM_DAY_GAP) {
        nextDate.setDate(nextDate.getDate() + 7);
      }
      return nextDate;
    }

    case "monthly":
    case "quarterly": {
      const monthGap = settings.billingFrequency === "quarterly" ? QUARTERLY_MINIMUM_MONTH_GAP : 1;
      let monthOffset = 0;
      let nextDate = monthlyPatternDate(settings.monthlyPattern, settings.monthlyDay, today.getFullYear(), today.getMonth());

      while (
        nextDate <= today ||
        (lastGenerated && monthsBetween(lastGenerated, nextDate) < monthGap)
      ) {
        monthOffset += 1;
        nextDate = monthlyPatternDate(
          settings.monthlyPattern,
          settings.monthlyDay,
          today.getFullYear(),
          today.getMonth() + monthOffset,
        );
      }

      return nextDate;
    }

    default:
      return null;
  }
}
